import { ServiceRegistry } from '../contexts/ServiceContext';
import { WebStorageService } from './web/WebStorageService';
import { WebSessionStorageService } from './web/WebSessionStorageService';
import { WebNetworkService } from './web/WebNetworkService';
import { WebBiometricService } from './web/WebBiometricService';
import { WebAnalyticsService } from './web/WebAnalyticsService';
import { WebConnectivityService } from './web/WebConnectivityService';
import { WebEventService } from './web/WebEventService';
import { WebRealtimeService } from './web/WebRealtimeService';
import { IdempotencyInterceptor } from './interceptors/IdempotencyInterceptor';
import { TraceInterceptor } from './interceptors/TraceInterceptor';
import { SanitizationInterceptor } from './interceptors/SanitizationInterceptor';
import ConfigurationService from './ConfigurationService';

/**
 * Singleton instance of the Service Registry for the Web platform.
 * This should be passed to the ServiceProvider at the root of the application.
 */
// Singletons for lazy loading
let storageInstance: WebStorageService | null = null;
let sessionStorageInstance: WebSessionStorageService | null = null;
let networkInstance: WebNetworkService | null = null;
let biometricInstance: WebBiometricService | null = null;
let analyticsInstance: WebAnalyticsService | null = null;
let connectivityInstance: WebConnectivityService | null = null;
let eventsInstance: WebEventService | null = null;
let realtimeInstance: WebRealtimeService | null = null;

/**
 * Lazy loaded getters for the Service Registry on the Web platform.
 * Decouples service instantiation from module-load time, ensuring
 * that ConfigurationService.initialize() has completed before services 
 * are constructed.
 */
export const webServiceRegistry: ServiceRegistry = {
    get storage(): WebStorageService {
        if (!storageInstance) {
            storageInstance = new WebStorageService();
        }
        return storageInstance;
    },
    get sessionStorage(): WebSessionStorageService {
        if (!sessionStorageInstance) {
            sessionStorageInstance = new WebSessionStorageService();
        }
        return sessionStorageInstance;
    },
    get network(): WebNetworkService {
        if (!networkInstance) {
            networkInstance = new WebNetworkService();
            // Initialize network interceptors lazily
            networkInstance.addInterceptor(new IdempotencyInterceptor());
            networkInstance.addInterceptor(new TraceInterceptor());
            networkInstance.addInterceptor(new SanitizationInterceptor());
        }
        return networkInstance;
    },
    get biometric(): WebBiometricService {
        if (!biometricInstance) {
            biometricInstance = new WebBiometricService();
        }
        return biometricInstance;
    },
    get analytics(): WebAnalyticsService {
        if (!analyticsInstance) {
            analyticsInstance = new WebAnalyticsService();
        }
        return analyticsInstance;
    },
    get connectivity(): WebConnectivityService {
        if (!connectivityInstance) {
            connectivityInstance = new WebConnectivityService();
        }
        return connectivityInstance;
    },
    get events(): WebEventService {
        if (!eventsInstance) {
            eventsInstance = new WebEventService();
        }
        return eventsInstance;
    },
    get realtime(): WebRealtimeService {
        if (!realtimeInstance) {
            realtimeInstance = new WebRealtimeService(ConfigurationService.getSocketUrl());
        }
        return realtimeInstance;
    },
};

/**
 * Accessor for the active service registry.
 * Primarily for use in non-React contexts (stores, static utilities).
 * Platform-agnostic by design.
 */
let activeRegistry: ServiceRegistry = webServiceRegistry;

export const getServiceRegistry = (): ServiceRegistry => activeRegistry;

export const setServiceRegistry = (registry: ServiceRegistry): void => {
    activeRegistry = registry;
};


// Initialize specific service configurations if needed
// Example: webServiceRegistry.network.addInterceptor(new AuthInterceptor());
